import styled from '@emotion/styled';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import BaseMarkdown from '../ui/Markdown';
import StartButton from '../ui/StartButton';
import background from './homeAssets/background.svg';
import greenDrop from './homeAssets/greenDrop.svg';
import Logo from './homeAssets/logo.svg';

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useState } from 'react';
import Button from '../ui/Button';
import CnmLogo from './homeAssets/partners/cnm.png';
import ArraLogo from './homeAssets/partners/logo-ARRA.png';
import OndesDurablesLogo from './homeAssets/partners/ondes-durables.png';
import RegionLogo from './homeAssets/partners/region-color.png';

const TopBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100px;
  background-color: var(--PURPLE);
  width: 100%;
  z-index: 1;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  background-color: #e4e2fe;
  min-height: 100vh;
  background-image: url(${background});
  background-repeat: no-repeat;
  background-position: right bottom;
  background-size: cover;
  z-index: 0;
`;

const Main = styled.div`
  z-index: 1;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;

  @media (max-width: 1000px) {
    flex-direction: column;
    align-items: center;
    justify-content: center;

    > * {
      width: 100% !important;
      margin-right: 0 !important;
      margin-left: 0 !important;
    }
  }
`;

const LeftSection = styled.div`
  color: #4d0042;
  width: 70%;
  margin-right: -10rem;
  padding-top: 5%;
  padding-right: 18%;
  padding-left: 5%;
  min-height: 700px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  background-image: url(${greenDrop});
  background-repeat: no-repeat;
  background-position: left center;
  background-size: contain;

  h1 {
    font-size: 2.5rem;
    line-height: 1.15;
    font-weight: 800;
    letter-spacing: -0.02em;
    margin: 0 0 0.75rem 0;
    width: 80%;
  }

  @media (min-width: 1800px) {
    h1 {
      width: 70%;
    }
  }

  @media (min-width: 2200px) {
    h1 {
      width: 65%;
    }
  }
`;

const Baseline = styled.p`
  font-size: clamp(1rem, 1.5vw, 1.25rem);
  line-height: 1.6;
  font-weight: 400;
  margin: 0 0 1.25rem 0;
  max-width: 48ch;
  color: #3b0034;
  opacity: 0.9;
`;

const Ctas = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  margin-top: 0.5rem;
  flex-wrap: wrap;
`;

const RightSection = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #4d0042;
  text-align: center;
  padding-top: 1rem;
  padding-bottom: 3rem;

  img {
    width: clamp(260px, 32vw, 450px);
    margin-bottom: 1rem;
  }

  h2 {
    color: white;
    font-size: clamp(1rem, 1.8vw, 1.5rem);
    font-weight: 300;
    line-height: 2.2;
    max-width: 550px;
    text-align: left;
    padding-left: clamp(2rem, 6vw, 6rem);
    padding-right: 2rem;
    -webkit-text-stroke: 10px #9da9ed;
    paint-order: stroke fill;
  }
`;

const PartnerLogos = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  padding: 1rem;
  img {
    max-height: 100px;
  }
`;

const Footer = styled.div`
  width: 100%;
  padding: 1rem;
  background-color: #f5f5f5;
  color: #4d0042;
  font-size: 0.8rem;
  text-align: center;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;

  @media (max-width: 1000px) {
    flex-direction: column;
  }
`;

const LegalButton = styled(Button)`
  text-transform: none !important;
`;

const Markdown = styled(BaseMarkdown)`
  font-size: 0.95rem;
  line-height: 1.5 !important;

  ul {
    list-style-position: inside;
  }

  li {
    margin-bottom: 0.25rem !important;
  }

  p {
    margin-bottom: 0.75rem !important;
  }
`;

const FooterMarkdown = styled(Markdown)`
  text-align: right;
  padding-right: 1rem;
  white-space: inherit !important;

  @media (max-width: 1000px) {
    text-align: center;
  }
`;

const Home = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['authentication']);
  const [open, setOpen] = useState(false); // Mentions légales
  const [infoOpen, setInfoOpen] = useState(false); // En savoir plus

  return (
    <>
      <Container>
        <TopBar />
        <Main>
          <LeftSection>
            <h1>
              <Trans i18nKey="authentication:home.title" /></h1>
            <Baseline>{t('authentication:home.baseline')}</Baseline>
            <Ctas>
              <StartButton
                onClick={() => setInfoOpen(true)}
                variantType="secondary"
              >
                {t('authentication:home.learn-more', 'En savoir plus')}
              </StartButton>
              <StartButton
                onClick={() => navigate(`/authentication/login`)}
                endIcon={<ArrowForwardIcon />}
              >
                {t('authentication:home.start')}
              </StartButton>
            </Ctas>
          </LeftSection>
          <RightSection>
            <img alt="Logo" src={Logo} />
            <h2>{t('authentication:home.sonar-full-name')}</h2>
          </RightSection>
        </Main>
        <Footer>
          <PartnerLogos>
            <a
              href="https://radios-arra.fr/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={ArraLogo} alt="ARRA Logo" />
            </a>
            <img src={OndesDurablesLogo} alt="Ondes Durables Logo" />
            <a
              href="https://www.laregion.fr/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={RegionLogo} alt="Region Occitanie Logo" />
            </a>
            <a
              href="https://www.cnm.fr/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={CnmLogo} alt="CNM Logo" />
            </a>
          </PartnerLogos>
          <div>
            <LegalButton onClick={() => setOpen(true)}>
              Mentions Légales
            </LegalButton>
          </div>
        </Footer>

        {/* Modale EN SAVOIR PLUS */}
        <Dialog
          open={infoOpen}
          onClose={() => setInfoOpen(false)}
          aria-labelledby="info-dialog-title"
          fullWidth
          maxWidth="md"
        >
          <DialogTitle id="info-dialog-title">
            {t('authentication:home.learn-more-title', 'En savoir plus')}
          </DialogTitle>
          <DialogContent dividers>
            <Markdown>{t('authentication:home.text')}</Markdown>
          </DialogContent>
          <DialogActions>
            <StartButton
              onClick={() => setInfoOpen(false)}
              endIcon={<ArrowForwardIcon />}
            >
              {t('common:ok')}
            </StartButton>
          </DialogActions>
        </Dialog>

        {/* Modale Mentions légales */}
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          aria-labelledby="legal-dialog-title"
          fullWidth
          maxWidth="md"
        >
          <DialogTitle id="legal-dialog-title">Mentions Légales</DialogTitle>
          <DialogContent dividers>
            <FooterMarkdown>
              {t('authentication:home.footer-text')}
            </FooterMarkdown>
          </DialogContent>
        </Dialog>
      </Container>
    </>
  );
};

export default Home;
